/**
 * PTBFFixationFormDialog — Create / Edit PTBF Fixation (chốt giá).
 *
 * Map theo Access `frmPurchaseQryPTBFFixationAdd` (14 field, TabIndex 1-13).
 * Validation theo VBA `btnSavePurchase_Click` errCheck:
 *   2 → contract_no rỗng    | 3 → fixation_date rỗng
 *   4 → lot_fixed rỗng      | 7 → lot_tobe_fixed=0 & lot_fixed≠0
 *   6 → lot_fixed > tbf     | 5 → market_level rỗng
 *
 * Edit mode (Access `btnEdit_Click` set `Enabled=False`):
 *   - contract_no, fixation_date → readonly.
 *   - lot_to_be_fixed = lookup(view.total_lot_tobe_fixed) + initialValue.lot_fixed
 *     (cộng lại để hiện "trần khả dụng" bao gồm phần đã chốt của record này).
 *     Lookup qua `GET /purchase/ptbf-fixations/lot-tobe-fixed?contract_no=`.
 *
 * Auto-fill khi chọn contract (Access `contract_no_AfterUpdate`, RowSource
 * columns `contract_no, supplier_name, quality_code, total_lot, futures_month`):
 *   cp_name = supplier_name; quality_code, total_lot, futures_month auto;
 *   lot_to_be_fixed = lookup(view.total_lot_tobe_fixed) (fallback total_lot,
 *   clear nếu cả 2 đều rỗng).
 */
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import type { HTMLAttributes, Key } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleSx, numSx, roSx } from './dialogStyle'

import { usePagePermission } from '@/hooks/usePagePermission'
import { useSnackbar } from '@/hooks/useSnackbar'
import { useListLocalPQuery } from '@/store/api/localPApi'
import {
  useCreatePTBFFixationMutation,
  useLazyGetPTBFLotTobeFixedQuery,
  useUpdatePTBFFixationMutation,
} from '@/store/api/ptbfFixationsApi'
import type {
  LocalPListItem,
  PTBFFixationCreateIn,
  PTBFFixationOut,
  PTBFFixationUpdateIn,
} from '@/types/api'
import { getErrorMessage } from '@/utils/error'

export interface PTBFFixationFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  /** Khi mở từ contract detail thì lock contract_no + ẩn autocomplete. */
  fixedContractNo?: string
  initialValue?: PTBFFixationOut | null
  onClose: () => void
  onSaved?: (id: number) => void
}

interface FixationFormValues {
  contract_no: string
  fixation_date: string
  issued_date: string
  is_upload: boolean
  /** Single string (Access: text auto từ contract.quality_code, không multi-select). */
  quality_code: string
  total_lot: number | ''
  lot_fixed: number | ''
  lot_to_be_fixed: number | ''
  market_level: number | ''
  futures_month: string
  cross_check_fix_level: string
  note: string
}

const EMPTY: FixationFormValues = {
  contract_no: '',
  fixation_date: dayjs().format('YYYY-MM-DD'),
  issued_date: '',
  is_upload: false,
  quality_code: '',
  total_lot: '',
  lot_fixed: '',
  lot_to_be_fixed: '',
  market_level: '',
  futures_month: '',
  cross_check_fix_level: '',
  note: '',
}

const schema: yup.ObjectSchema<FixationFormValues> = yup.object({
  // errCheck=2
  contract_no: yup.string().trim().required('Please choose Contract No!'),
  // errCheck=3
  fixation_date: yup.string().required('Please choose Fixation date!'),
  issued_date: yup.string().default(''),
  is_upload: yup.boolean().default(false),
  quality_code: yup.string().default(''),
  total_lot: yup.mixed<number | ''>().default(''),
  // errCheck=4 + user-call: không cho phép lot_fixed ≤ 0.
  lot_fixed: yup
    .mixed<number | ''>()
    .test(
      'required',
      'Please type Lot fixed!',
      (v) => v !== '' && v !== undefined && v !== null,
    )
    .test(
      'positive',
      'Lot fixed must be greater than 0!',
      (v) => typeof v !== 'number' || v > 0,
    )
    .required(),
  lot_to_be_fixed: yup.mixed<number | ''>().default(''),
  // errCheck=5 — Access chỉ check rỗng (cleanCheck = ""), không chặn 0/âm.
  market_level: yup
    .mixed<number | ''>()
    .test(
      'required',
      'Please type Market level!',
      (v) => v !== '' && v !== undefined && v !== null,
    )
    .required(),
  futures_month: yup.string().default(''),
  cross_check_fix_level: yup.string().trim().default(''),
  note: yup.string().trim().default(''),
}) as yup.ObjectSchema<FixationFormValues>

function fromFixation(f: PTBFFixationOut): FixationFormValues {
  const n = (x: number | null | undefined): number | '' =>
    x === null || x === undefined ? '' : x
  const s = (x: string | null | undefined): string => x ?? ''
  // BE: list view trả `quality_code` là string concat (vd "Q1, Q2"); base table
  // trả array. UI hiện text đơn → lấy phần tử đầu (Access cũng chỉ giữ 1 string).
  let qc = ''
  const raw = f.quality_code
  if (Array.isArray(raw)) {
    qc = raw[0] ?? ''
  } else if (typeof raw === 'string') {
    qc = raw.split(',').map((x) => x.trim()).filter(Boolean)[0] ?? ''
  }
  return {
    contract_no: s(f.contract_no),
    fixation_date: s(f.fixation_date),
    issued_date: s(f.issued_date),
    is_upload: !!f.is_upload,
    quality_code: qc,
    total_lot: n(f.total_lot),
    lot_fixed: n(f.lot_fixed),
    lot_to_be_fixed: n(f.lot_to_be_fixed),
    market_level: n(f.market_level),
    futures_month: s(f.futures_month),
    cross_check_fix_level: s(f.cross_check_fix_level),
    note: s(f.note),
  }
}

/** CREATE payload — Access INSERT 6 field business + is_upload/issued_date (UPDATE phụ). */
function toApiCreate(v: FixationFormValues): PTBFFixationCreateIn {
  const num = (x: number | ''): number | null => (x === '' ? null : x)
  const str = (x: string): string | null => (x.trim() === '' ? null : x.trim())
  const qc = v.quality_code.trim()
  const ml = typeof v.market_level === 'number' ? v.market_level : 0
  return {
    contract_no: v.contract_no.trim(),
    fixation_date: v.fixation_date,
    market_level: Math.round(ml * 100) / 100,
    futures_month: str(v.futures_month),
    total_lot: num(v.total_lot),
    lot_to_be_fixed: num(v.lot_to_be_fixed),
    lot_fixed: num(v.lot_fixed),
    cross_check_fix_level: str(v.cross_check_fix_level),
    // BE schema vẫn nhận array → wrap [code] cho tương thích.
    quality_code: qc ? [qc] : null,
    note: str(v.note),
    is_upload: v.is_upload,
    issued_date: str(v.issued_date),
  }
}

/**
 * EDIT payload — Access `btnSavePurchase_Click` UPDATE chỉ 4 field business:
 *   `lot_fixed, market_level, cross_check_fix_level, note`
 * + UPDATE phụ: `is_upload, issued_date` (Access áp lên TOÀN contract_no).
 * Không gửi contract_no/fixation_date/total_lot/quality_code/futures_month —
 * Access không update các field này khi edit.
 */
function toApiEdit(v: FixationFormValues): PTBFFixationUpdateIn {
  const num = (x: number | ''): number | null => (x === '' ? null : x)
  const str = (x: string): string | null => (x.trim() === '' ? null : x.trim())
  const ml = typeof v.market_level === 'number' ? v.market_level : 0
  return {
    lot_fixed: num(v.lot_fixed),
    market_level: Math.round(ml * 100) / 100,
    cross_check_fix_level: str(v.cross_check_fix_level),
    note: str(v.note),
    is_upload: v.is_upload,
    issued_date: str(v.issued_date),
  }
}

const toInt = (raw: string): number | '' => {
  if (raw === '') return ''
  const n = Number(raw)
  return Number.isFinite(n) ? Math.trunc(n) : ''
}

/** Round về 2 decimals (Access `#,##0.00`). Trả '' khi không phải số. */
const round2 = (v: number | ''): number | '' => {
  if (v === '' || v === null || v === undefined) return ''
  if (typeof v !== 'number' || !Number.isFinite(v)) return ''
  return Math.round(v * 100) / 100
}

export function PTBFFixationFormDialog({
  open,
  mode,
  fixedContractNo,
  initialValue,
  onClose,
  onSaved,
}: PTBFFixationFormDialogProps) {
  const snack = useSnackbar()
  const { canEdit } = usePagePermission()
  const isEdit = mode === 'edit'
  const lockedByUpload = isEdit && !!initialValue?.is_upload

  const [createFixation, { isLoading: creating }] = useCreatePTBFFixationMutation()
  const [updateFixation, { isLoading: updating }] = useUpdatePTBFFixationMutation()
  const [fetchLotTobeFixed] = useLazyGetPTBFLotTobeFixedQuery()
  const loading = creating || updating

  // Contracts autocomplete — RowSource per Access: Differential, active.
  // (Access còn lọc `quantity_kg = quantity_kg_old`; BE `is_status=1` xấp xỉ
  // `forced_close_text='N'`. Filter no-amendment tạm bỏ qua, không phá UX.)
  //
  // `refetchOnMountOrArgChange: true` — `total_lot` / `lot_to_fix` đến từ MV
  // `public_mv_local_p` aggregation, đổi khi user khác vừa fix lots. Force fresh
  // mỗi lần dialog mount để autofill `lot_to_be_fixed` không stale (root cause:
  // validation `lot_fixed <= lot_to_be_fixed` sẽ tính sai trên data cũ).
  const { data: contractsList } = useListLocalPQuery(
    {
      contract_type: 'Differential',
      is_status: 1,
      page: 1,
      page_size: 1_000_000,
    },
    { refetchOnMountOrArgChange: true },
  )
  // Dedupe theo contract_no — list view đôi khi join trả nhiều row cho 1 contract,
  // gây Autocomplete warning "two children with the same key".
  const contractOptions: LocalPListItem[] = useMemo(() => {
    const items = contractsList?.items ?? []
    const seen = new Set<string>()
    const out: LocalPListItem[] = []
    for (const it of items) {
      const key = it.contract_no ?? ''
      if (!key || seen.has(key)) continue
      seen.add(key)
      out.push(it)
    }
    return out
  }, [contractsList])

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FixationFormValues>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY,
  })

  const selectedContractNo = watch('contract_no')
  const lotFixed = watch('lot_fixed')
  const lotToBeFixed = watch('lot_to_be_fixed')

  const selectedContract = useMemo(
    () => contractOptions.find((c) => c.contract_no === selectedContractNo) ?? null,
    [contractOptions, selectedContractNo],
  )

  const [lastFilledContractNo, setLastFilledContractNo] = useState('')

  useEffect(() => {
    if (!open) return
    setLastFilledContractNo('')
    if (isEdit && initialValue) {
      reset(fromFixation(initialValue))
      // Access btnEdit_Click: lot_tobe_fixed = view.total_lot_tobe_fixed +
      //   subForm.lot_fixed (cộng lại phần đã claim để cho phép re-allocate).
      // Phải query fresh từ view, không dùng snapshot trên row.
      const cno = initialValue.contract_no
      if (cno) {
        void fetchLotTobeFixed({ contract_no: cno })
          .unwrap()
          .then((res) => {
            const remaining = res.data?.total_lot_tobe_fixed
            const totalLot = res.data?.total_lot
            const base =
              remaining ?? totalLot ?? (initialValue.total_lot ?? null)
            const recLotFixed =
              typeof initialValue.lot_fixed === 'number' ? initialValue.lot_fixed : 0
            if (base !== null && base !== undefined) {
              setValue('lot_to_be_fixed', Math.trunc(Number(base) + recLotFixed))
            }
          })
          .catch(() => {
            /* fallback: giữ value từ fromFixation (snapshot) */
          })
      }
      setLastFilledContractNo(initialValue.contract_no ?? '')
    } else {
      reset({ ...EMPTY, contract_no: fixedContractNo ?? '' })
    }
  }, [open, isEdit, initialValue, fixedContractNo, reset, fetchLotTobeFixed, setValue])

  // Auto-fill khi contract đổi (create-mode only). Access `contract_no_AfterUpdate`:
  //   quality_code = col(2); cp_name = col(1); total_lot = col(3); futures_month = col(4)
  //   lot_tobe_fixed = view.total_lot_tobe_fixed (fallback total_lot, clear nếu cả 2 rỗng)
  useEffect(() => {
    if (isEdit) return
    if (!selectedContract) return
    if (!selectedContractNo) return
    if (lastFilledContractNo === selectedContractNo) return

    setValue('quality_code', selectedContract.quality_code ?? '')
    setValue('total_lot', selectedContract.total_lot ?? '')
    setValue('futures_month', selectedContract.futures_month ?? '')
    setLastFilledContractNo(selectedContractNo)

    // Access query `SELECT * FROM v_ptbf_fixation_p WHERE contract_no = ?`:
    //   rows > 0 → lot_tobe_fixed = total_lot_tobe_fixed
    //   else if total_lot ≠ "" → lot_tobe_fixed = total_lot
    //   else → clear
    void fetchLotTobeFixed({ contract_no: selectedContractNo })
      .unwrap()
      .then((res) => {
        const remaining = res.data?.total_lot_tobe_fixed
        const totalLot = res.data?.total_lot
        if (remaining !== null && remaining !== undefined) {
          setValue('lot_to_be_fixed', Math.trunc(Number(remaining)))
        } else if (totalLot !== null && totalLot !== undefined) {
          setValue('lot_to_be_fixed', Math.trunc(Number(totalLot)))
        } else {
          setValue('lot_to_be_fixed', '')
        }
      })
      .catch(() => {
        // Fallback nếu endpoint fail: dùng total_lot từ contract option.
        const tl = selectedContract.total_lot ?? null
        setValue('lot_to_be_fixed', tl ?? '')
      })
  }, [
    isEdit,
    selectedContract,
    selectedContractNo,
    lastFilledContractNo,
    setValue,
    fetchLotTobeFixed,
  ])

  // ---- errCheck 7 → 6 (sau khi yup required pass) ----
  const ERR_LOT_FIX_LOCKED = 'Lot fix can not be change'
  const ERR_LOT_EXCEED =
    'Lot fixed must be less than or equal to Lot tobe fixed (per Contract)'
  const lotWarning = useMemo(() => {
    const f = typeof lotFixed === 'number' ? lotFixed : null
    const tbf = typeof lotToBeFixed === 'number' ? lotToBeFixed : null
    if (f === null) return null
    if (tbf === 0 && f !== 0) return ERR_LOT_FIX_LOCKED
    if (tbf !== null && f > tbf) return ERR_LOT_EXCEED
    return null
  }, [lotFixed, lotToBeFixed])

  const onSubmit = handleSubmit(
    async (values) => {
      // Access errCheck 7/6 chạy giữa 4 và 5 — yup đã chặn 2/3/4/5; chèn 7/6 ở đây.
      if (lotWarning) {
        // Access errCheck=7 còn force `Me.lot_fixed = "0"` sau khi show MsgBox.
        if (lotWarning === ERR_LOT_FIX_LOCKED) {
          setValue('lot_fixed', 0)
        }
        snack.error(lotWarning)
        return
      }
      try {
        if (isEdit && initialValue) {
          // EDIT — Access UPDATE 4 field business + UPDATE phụ is_upload/issued_date.
          const res = await updateFixation({
            id: initialValue.ptbf_p_id,
            body: toApiEdit(values),
          }).unwrap()
          snack.success('Successfully Updated')
          onSaved?.(res.data?.ptbf_p_id ?? initialValue.ptbf_p_id)
        } else {
          // CREATE — Access INSERT 6 field business + UPDATE phụ.
          // NOTE (gap BE): Access còn áp is_upload/issued_date cho TOÀN contract_no
          //   (WHERE contract_no = ? AND is_status = 0), BE hiện chỉ update row này.
          //   Cần BE bổ sung propagate — đã spawn task riêng.
          const res = await createFixation(toApiCreate(values)).unwrap()
          snack.success('Successfully Added')
          onSaved?.(res.data?.ptbf_p_id ?? 0)
        }
        onClose()
      } catch (err) {
        snack.error(
          getErrorMessage(
            err,
            isEdit ? 'Failed to update fixation' : 'Failed to create fixation',
          ),
        )
      }
    },
    () => snack.error('Please fix the highlighted fields'),
  )

  const cpDisplay = useMemo(() => {
    if (selectedContract) {
      return `${selectedContract.supplier_code ?? ''} — ${selectedContract.supplier_name ?? ''}`.replace(
        /^ — /,
        '',
      )
    }
    if (isEdit && initialValue) {
      return initialValue.name ?? ''
    }
    return ''
  }, [selectedContract, isEdit, initialValue])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" sx={{ flex: 1, fontWeight: 700, fontSize: '1rem' }}>
          Purchase - PTBF Fixation
          {isEdit && initialValue?.ptbf_p_id ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              #{initialValue.ptbf_p_id}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => void onSubmit()}
            disabled={loading || lockedByUpload || !canEdit}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button variant="outlined" size="small" onClick={onClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {lockedByUpload && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Fixation has been uploaded (issued) — read-only.
          </Alert>
        )}

        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          {/* Row 1 (Access Top=300): ID (6) | (empty 6) */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="ID"
              size="small"
              fullWidth
              value={isEdit ? initialValue?.ptbf_p_id ?? '' : ''}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              helperText={!isEdit ? 'Auto-generated on save' : undefined}
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} />

          {/* Row 2 (Access Top=840): FixationDate (TabIndex=1) | IssuedDate (TabIndex=2) */}
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="fixation_date"
              render={({ field }) => (
                <DatePicker
                  label="FixationDate *"
                  format="DD-MMM-YYYY"
                  disabled={lockedByUpload || isEdit}
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      error: !!errors.fixation_date,
                      helperText: errors.fixation_date?.message,
                      inputProps: { tabIndex: 1 },
                      sx: isEdit ? roSx : undefined,
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="issued_date"
              render={({ field }) => (
                <DatePicker
                  label="IssuedDate"
                  format="DD-MMM-YYYY"
                  disabled={lockedByUpload}
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      inputProps: { tabIndex: 2 },
                    },
                  }}
                />
              )}
            />
          </Grid>

          {/* Row 3 (Access Top=1380): Contract No. (TabIndex=3) | Is the contract uploaded? (TabIndex=4) */}
          <Grid item xs={12} sm={6}>
            {fixedContractNo ? (
              <TextField
                label="Contract No."
                size="small"
                fullWidth
                value={fixedContractNo}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={roSx}
              />
            ) : (
              <Controller
                control={control}
                name="contract_no"
                render={({ field }) => {
                  const found = contractOptions.find((c) => c.contract_no === field.value)
                  const selected =
                    found ??
                    (field.value
                      ? ({
                          contract_no: field.value,
                          supplier_name: null,
                          supplier_code: null,
                        } as LocalPListItem)
                      : null)
                  return (
                    <Autocomplete
                      size="small"
                      options={contractOptions}
                      value={selected}
                      onChange={(_e, v) => field.onChange(v?.contract_no ?? '')}
                      getOptionLabel={(opt) =>
                        opt
                          ? `${opt.contract_no} — ${opt.supplier_name ?? opt.supplier_code ?? ''}`
                          : ''
                      }
                      isOptionEqualToValue={(o, v) => o.contract_no === v.contract_no}
                      disabled={isEdit || lockedByUpload}
                      renderOption={(props, opt) => {
                        const { key: _omit, ...rest } = props as HTMLAttributes<HTMLLIElement> & {
                          key?: Key
                        }
                        return (
                          <li {...rest} key={opt.contract_no}>
                            {opt.contract_no} — {opt.supplier_name ?? opt.supplier_code ?? ''}
                          </li>
                        )
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Contract No. *"
                          error={!!errors.contract_no}
                          helperText={errors.contract_no?.message}
                          inputProps={{ ...params.inputProps, tabIndex: 3 }}
                          sx={isEdit || lockedByUpload ? roSx : undefined}
                        />
                      )}
                    />
                  )
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
            <Controller
              control={control}
              name="is_upload"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={field.value}
                      disabled={lockedByUpload}
                      onChange={(e) => field.onChange(e.target.checked)}
                      inputProps={{ tabIndex: 4 }}
                    />
                  }
                  label="Is the contract uploaded?"
                />
              )}
            />
          </Grid>

          {/* Row 4 (Access Top=1920): CP Name (12 — readonly, TabIndex=5) */}
          <Grid item xs={12}>
            <TextField
              label="CP Name"
              size="small"
              fullWidth
              value={cpDisplay}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 5 }}
              sx={roSx}
            />
          </Grid>

          {/* Row 5 (Access Top=2460): Quality Code (TabIndex=6 — readonly) | Total Lot (TabIndex=7 — readonly) */}
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="quality_code"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Quality Code"
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ tabIndex: 6 }}
                  sx={roSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="total_lot"
              render={({ field }) => (
                <TextField
                  label="Total Lot"
                  type="number"
                  size="small"
                  fullWidth
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) => field.onChange(toInt(e.target.value))}
                  onBlur={field.onBlur}
                  inputProps={{ inputMode: 'numeric', step: 1, readOnly: true, tabIndex: 7 }}
                  disabled
                  error={!!errors.total_lot}
                  helperText={errors.total_lot?.message}
                  sx={[numSx, roSx]}
                />
              )}
            />
          </Grid>

          {/* Row 6 (Access Top=3012): Lot fixed (TabIndex=8) | Lot tobe fixed (TabIndex=9 — readonly) */}
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="lot_fixed"
              render={({ field }) => (
                <TextField
                  label="Lot fixed *"
                  type="number"
                  size="small"
                  fullWidth
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) => field.onChange(toInt(e.target.value))}
                  onBlur={field.onBlur}
                  inputProps={{ inputMode: 'numeric', step: 1, min: 1, tabIndex: 8 }}
                  disabled={lockedByUpload}
                  error={!!errors.lot_fixed}
                  helperText={errors.lot_fixed?.message}
                  sx={numSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="lot_to_be_fixed"
              render={({ field }) => (
                <TextField
                  label="Lot tobe fixed (per Contract)"
                  type="number"
                  size="small"
                  fullWidth
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) => field.onChange(toInt(e.target.value))}
                  onBlur={field.onBlur}
                  inputProps={{ inputMode: 'numeric', step: 1, readOnly: true, tabIndex: 9 }}
                  disabled
                  error={!!errors.lot_to_be_fixed}
                  helperText={errors.lot_to_be_fixed?.message}
                  sx={[numSx, roSx]}
                />
              )}
            />
          </Grid>

          {lotWarning && (
            <Grid item xs={12}>
              <Alert severity="warning">{lotWarning}</Alert>
            </Grid>
          )}

          {/* Row 7 (Access Top=3564): Market Level (TabIndex=10) | Futures Month (TabIndex=11 — readonly) */}
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="market_level"
              render={({ field }) => (
                <TextField
                  label="Market Level *"
                  type="number"
                  size="small"
                  fullWidth
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  onBlur={(e) => {
                    // Round về 2 decimals (Access `#,##0.00`).
                    const v = e.target.value === '' ? '' : Number(e.target.value)
                    field.onChange(round2(v as number | ''))
                    field.onBlur()
                  }}
                  inputProps={{ step: '0.01', inputMode: 'decimal', tabIndex: 10 }}
                  disabled={lockedByUpload}
                  error={!!errors.market_level}
                  helperText={errors.market_level?.message}
                  sx={numSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="futures_month"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Futures Month"
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ tabIndex: 11 }}
                  sx={roSx}
                />
              )}
            />
          </Grid>

          {/* Row 8 (Access Top=4116): Crosscheck fixed level (TabIndex=12) */}
          <Grid item xs={12}>
            <Controller
              control={control}
              name="cross_check_fix_level"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Crosscheck fixed level"
                  size="small"
                  fullWidth
                  disabled={lockedByUpload}
                  inputProps={{ tabIndex: 12 }}
                  error={!!errors.cross_check_fix_level}
                  helperText={errors.cross_check_fix_level?.message}
                />
              )}
            />
          </Grid>

          {/* Row 9 (Access Top=4668): Note (TabIndex=13 — multiline) */}
          <Grid item xs={12}>
            <Controller
              control={control}
              name="note"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Note"
                  size="small"
                  multiline
                  minRows={2}
                  fullWidth
                  disabled={lockedByUpload}
                  inputProps={{ tabIndex: 13 }}
                  error={!!errors.note}
                  helperText={errors.note?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

export default PTBFFixationFormDialog
